import React, { useEffect, useState, forwardRef, useMemo, useCallback } from 'react';
import { find, isFunction , uniqBy, isEmpty } from 'lodash';
import ToolsIntegrationsAPI from 'restAPI/ToolsIntegrationAPI';
import message from "components/CustomMessage";
import GenericMappingForm from '../GenericMappingForm';
import { FieldTypes } from 'utils/formUtils';

const MappingFormWithApplicationId = forwardRef(({disableProjectFields = false, extraField = [], form, pagination = false, multiSelect = false,  ...props}, ref) => {
  const [projects, setProjects] = useState([]);

    const setOptionsFromPagination = (option) => {
        setOptions(prev => uniqBy([...prev,...option], 'id'));
    };

    const [mappedProjectList] = useState(() => {
      // this state contains the already mapped application/site
      // This is stored because the project and version dropdowns are paginated
      // and if the option selected is not on 2st page, then the ID is displayed as this is how select works
      if (props.record && pagination) {
        const { applicationId, applicationName } = props.record;
        return {
          applicationId: [{ id: applicationId, name: applicationName }],
        }
      }
      return { applicationId: [] }
    })

    const [options, setOptions] = useState(mappedProjectList.applicationId);
    const fetchRepos = useCallback((params) => {
        const { promise,cancel } =  ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, params);
        promise.then((data) => {
        if(mappedProjectList["applicationId"].length > 0 && !isEmpty(data.projects)){
          data.projects = [...data.projects, ...mappedProjectList["applicationId"]];
        }
          setOptionsFromPagination(data.projects)
        })
        return { promise,cancel}
    }, [props.toolName, mappedProjectList]);


  useEffect(() => {
    if(!pagination){
      try {
        const { promise, cancel } = ToolsIntegrationsAPI.getGenericProjectByToolName(props.toolName, { login_id: props.loginConfigId });
        promise.then((response) => {
          const { projects } = response;
          setProjects(projects);
        });

        return cancel;
      } catch (err) {
        message.error('Problem fetching projects')
      }
  }
  }, [props.loginConfigId, props.toolName, pagination]);

  const getExtraFieldsData = (config) => {
    const { applicationId } = config;
    let _applicationName;
    if(multiSelect){
      const data = (pagination ? options : projects).reduce((acc,cv) =>{
        const { id, name } = cv;
      
        if(applicationId.includes(id)){
          acc.name.push(name);
        }
        return acc;
      },{name:[]});

      _applicationName = data.name.join();
    }
    else{
      _applicationName = find(pagination ? options : projects, (obj) => obj.id === applicationId)?.name;
    }

    const extraData = isFunction(props.getExtraData) ? props.getExtraData(config) : {}
    
    return ({
      applicationId: multiSelect ? applicationId.join() : applicationId,
      applicationName: _applicationName,
      ...extraData
    });
  };

  const selectProps = useMemo(() => (pagination ? {api: fetchRepos, apiParams:  {login_id: props.loginConfigId }} : {}), [pagination, fetchRepos, props.loginConfigId])

  const extraFields = useMemo(() => ([
    {
      name: 'applicationId',
      colSpan: '12',
      type: pagination ? FieldTypes.SELECT_WITH_PAGINATED_DATA : FieldTypes.SELECT,
      label: <>{props.toolName} Project</>,
      options: !pagination && projects.map(option => {
        const { label = option.name, value = option.id } = option;
        return { label, value }
      }),
      disabled: disableProjectFields,
      rules: [{ required: true, message: `${props.toolName} Project is required` }],
      props: {
        autoFocus: true,
        mode: multiSelect && "multiple",
        ...selectProps
      }
    },
    ...extraField
  ]), [disableProjectFields, projects, props.toolName, extraField, selectProps, pagination, multiSelect]);

  return (
    <GenericMappingForm
      ref={ref}
      form={form}
      toolName={props.toolName}
      projects={projects}
      getExtraFieldsData={getExtraFieldsData}
      parseExtraFieldsDataFromResponse={props.parseExtraFieldsData}
      extraFields={extraFields}
      {...props}
    />
  )
})

export default MappingFormWithApplicationId;